import type { AddressFieldDefinitionDto, CountryDto } from '@_/api-contracts/common.ts';
import type { addressSchema } from '@_/form-validators';
import type { z } from 'zod';
import { useSelector } from '@tanstack/react-form';
import clsx from 'clsx';
import { useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { SharedConfig } from '@_/config/shared-config.ts';
import { labelCommonI18n } from '@_/i18n-common';
import { CountryCodes } from '@_/static';
import { NativeSelectOption, Skeleton, SkeletonInput } from '@_/ui';
import {
  BaseForm,
  FormI18nTextInput,
  FormNativeSelect,
  FormTextInput
} from '../formElements/index.ts';

export const addressFieldGroupDefaultValue = (): AddressFieldGroupSchema => ({
  countryCode: 'HK',
  postalCode: '',
  zone1Code: '',
  zone2Code: '',
  locality: {},
  line1: {},
  line2: {}
});

interface AddressFieldGroupProps {
  addressFieldDefinition?: AddressFieldDefinitionDto;
  countryZones?: CountryDto;
  language: string;
  loading?: boolean;
}

type AddressFieldGroupSchema = z.input<ReturnType<typeof addressSchema>>;

export const AddressFieldGroup = BaseForm.withFieldGroup({
  defaultValues: addressFieldGroupDefaultValue(),
  props: {} as AddressFieldGroupProps,
  render: function Render({
    group,
    addressFieldDefinition,
    countryZones,
    language,
    loading = false
  }) {
    const intl = useIntl();

    const prevCountryCodeRef = useRef(group.getFieldValue('countryCode'));

    const zone1Code = useSelector(group.store, state => state.values.zone1Code);

    const resetFields = () => {
      const countryCodeMeta = group.getFieldMeta('countryCode');
      if (!countryCodeMeta || countryCodeMeta.isDefaultValue) {
        group.resetField('postalCode');
        group.resetField('zone1Code');
        group.resetField('zone2Code');
        group.resetField('locality');
        group.resetField('line1');
        group.resetField('line2');
      } else {
        group.setFieldValue('postalCode', '');
        group.setFieldValue('zone1Code', '');
        group.setFieldValue('zone2Code', '');
        group.setFieldValue('locality', {});
        group.setFieldValue('line1', {});
        group.setFieldValue('line2', {});
      }

      void group.validateField('postalCode', 'change');
      void group.validateField('zone1Code', 'change');
      void group.validateField('zone2Code', 'change');
      void group.validateField('locality', 'change');
      void group.validateField('line1', 'change');
      void group.validateField('line2', 'change');
    };

    const countryNames = useMemo(() => {
      const countryFormatter = new Intl.DisplayNames(language, {
        type: 'region',
        style: 'short'
      });

      return CountryCodes.map(code => ({
        code,
        name: countryFormatter.of(code) ?? ''
      })).sort((a, b) => (a.name < b.name ? -1 : 1));
    }, [language]);

    if (loading) {
      return (
        <Skeleton className="flex flex-col gap-y-4">
          <SkeletonInput />
          <SkeletonInput />
          <SkeletonInput />
          <div className="grid grid-cols-2 gap-x-2 gap-y-4">
            <SkeletonInput />
            <SkeletonInput />
          </div>
        </Skeleton>
      );
    }

    let inputs: Array<{ key: string; elements: React.ReactNode[] }> = [];

    if (addressFieldDefinition && countryZones) {
      inputs = addressFieldDefinition.fields.map((row, index) => ({
        key: `row-${index}`,
        elements: row.map(field => {
          const label =
            addressFieldDefinition.labels[field as keyof typeof addressFieldDefinition.labels];

          if (field === 'countryCode') {
            return (
              <group.AppField
                key={field}
                name="countryCode"
                children={() => (
                  <FormNativeSelect disabled>
                    <NativeSelectOption>
                      {intl.formatMessage(labelCommonI18n.countryOrRegion)}
                    </NativeSelectOption>
                    {countryNames.map(country => (
                      <NativeSelectOption key={country.code} value={country.code}>
                        {country.name}
                      </NativeSelectOption>
                    ))}
                  </FormNativeSelect>
                )}
                listeners={{
                  onChange: ({ value }) => {
                    if (value && value !== prevCountryCodeRef.current) {
                      prevCountryCodeRef.current = value;
                      resetFields();
                    }
                  }
                }}
              />
            );
          }

          if (field === 'zone1Code') {
            return (
              <group.AppField
                key={field}
                name="zone1Code"
                children={() => (
                  <FormNativeSelect>
                    <NativeSelectOption disabled value="">
                      {label}
                    </NativeSelectOption>
                    {countryZones.zones?.map(zone1 => (
                      <NativeSelectOption key={zone1.code} value={zone1.code}>
                        {zone1.name}
                      </NativeSelectOption>
                    ))}
                  </FormNativeSelect>
                )}
              />
            );
          }

          if (field === 'zone2Code') {
            return (
              <group.AppField
                key={field}
                name="zone2Code"
                children={() => (
                  <FormNativeSelect>
                    <NativeSelectOption disabled value="">
                      {label}
                    </NativeSelectOption>
                    {countryZones.zones
                      ?.find(zone => zone.code === zone1Code)
                      ?.zones?.map(zone2 => (
                        <NativeSelectOption key={zone2.code} value={zone2.code}>
                          {zone2.name}
                        </NativeSelectOption>
                      ))}
                  </FormNativeSelect>
                )}
              />
            );
          }

          if (field === 'postalCode') {
            return (
              <group.AppField
                key={field}
                name="postalCode"
                children={() => (
                  <FormTextInput
                    placeholder={label}
                    maxLength={SharedConfig.address.postalCodeMaxLength}
                  />
                )}
              />
            );
          }

          if (field === 'locality' || field === 'line1' || field === 'line2') {
            const maxLength =
              field === 'locality'
                ? SharedConfig.address.postalCodeMaxLength
                : field === 'line1'
                  ? SharedConfig.address.line1MaxLength
                  : SharedConfig.address.line2MaxLength;
            return (
              <group.AppField
                key={field}
                name={field}
                children={() => (
                  <FormI18nTextInput
                    languageCode={language}
                    placeholder={label}
                    showAll={false}
                    maxLength={maxLength}
                  />
                )}
              />
            );
          }

          throw new Error(`unknown address field ${String(field)}`);
        })
      }));
    }

    return (
      <div className="flex flex-col gap-y-4">
        {inputs.map(({ key, elements }) => (
          <div
            key={key}
            className={clsx(
              'grid gap-x-4 gap-y-4',
              elements.length === 2 && 'grid-cols-2',
              elements.length === 3 && 'sm:grid-cols-3',
              elements.length === 4 && 'sm:grid-cols-4'
            )}
          >
            {elements.map(element => element)}
          </div>
        ))}
      </div>
    );
  }
});
